import React from 'react';
import { aboutContent, aboutImage } from '../data/mock';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';

const About = () => {
  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="about" className="relative py-32 lg:py-40 bg-[#020617] overflow-hidden">
      {/* Animated Background with Gradient Waves */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/8 via-transparent to-[#020617] animate-gradient-flow-smooth" />
      <div className="absolute inset-0 bg-gradient-to-t from-transparent via-[#D4AF37]/3 to-transparent animate-gradient-wave-soft" />
      
      {/* Enhanced Floating Particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[20%] left-[18%] w-2 h-2 bg-[#D4AF37]/50 rounded-full animate-drift-smooth-1 blur-sm" />
        <div className="absolute top-[45%] right-[25%] w-1.5 h-1.5 bg-white/40 rounded-full animate-drift-smooth-2 blur-sm" />
        <div className="absolute bottom-[30%] left-[35%] w-2.5 h-2.5 bg-[#D4AF37]/40 rounded-full animate-drift-smooth-3 blur-sm" />
        <div className="absolute top-[60%] right-[15%] w-1 h-1 bg-white/50 rounded-full animate-drift-smooth-4 blur-sm" />
        <div className="absolute bottom-[15%] left-[45%] w-1.5 h-1.5 bg-[#D4AF37]/45 rounded-full animate-drift-smooth-5 blur-sm" />
        <div className="absolute top-[35%] left-[60%] w-1 h-1 bg-white/35 rounded-full animate-drift-smooth-6 blur-sm" />
      </div>

      {/* Smooth Light Beams */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-[25%] w-[1px] h-full bg-gradient-to-b from-transparent via-[#D4AF37]/20 to-transparent animate-beam-smooth-1" />
        <div className="absolute top-0 right-[30%] w-[1px] h-full bg-gradient-to-b from-transparent via-white/15 to-transparent animate-beam-smooth-2" />
      </div>

      {/* Pulsing Ambient Glow */}
      <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-[#D4AF37]/8 rounded-full blur-3xl animate-pulse-ambient-1" />
      <div className="absolute bottom-1/3 left-1/3 w-56 h-56 bg-[#D4AF37]/6 rounded-full blur-3xl animate-pulse-ambient-2" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          
          {/* Left - Image Section */}
          <div className="relative order-2 lg:order-1 animate-scale-in-smooth">
            <div className="relative group">
              <img
                src={aboutImage}
                alt="Sydney Australia"
                className="w-full h-[600px] object-cover transition-transform duration-1500 group-hover:scale-110"
              />
              
              {/* Animated Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#020617]/85 via-[#020617]/45 to-transparent" />
              <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/0 via-[#D4AF37]/8 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-1000" />
              
              {/* Shimmer Effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/15 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1500" />
              
              {/* Glass Overlay Label */}
              <div className="absolute bottom-8 left-8 bg-[#0f172a]/95 backdrop-blur-md border border-white/15 px-6 py-3 transition-all duration-1000 group-hover:bg-[#0f172a]/98 group-hover:border-[#D4AF37]/40 group-hover:scale-105">
                <div className="text-[#D4AF37] text-sm font-serif tracking-wide animate-text-pulse">Sydney, Australia</div>
              </div>
              
              {/* Decorative Corner Borders with Smooth Animation */}
              <div className="absolute -top-4 -left-4 w-28 h-28 border-t border-l border-[#D4AF37] animate-border-draw-smooth" />
              <div className="absolute -bottom-4 -right-4 w-28 h-28 border-b border-r border-[#D4AF37] animate-border-draw-smooth animation-delay-400" />
              
              {/* Pulsing Light Corner */}
              <div className="absolute top-0 left-0 w-24 h-24 bg-[#D4AF37]/25 blur-2xl animate-pulse-corner-soft" />
              <div className="absolute bottom-0 right-0 w-24 h-24 bg-[#D4AF37]/20 blur-2xl animate-pulse-corner-soft animation-delay-500" />
            </div>
          </div>

          {/* Right - Content Section */}
          <div className="order-1 lg:order-2 animate-slide-from-right-smooth">
            {/* Section Label */}
            <div className="flex items-center gap-6 mb-8 animate-fade-slide-in-smooth">
              <div className="w-16 h-[1px] bg-[#D4AF37] animate-width-expand-smooth" />
              <span className="text-[#D4AF37] text-xs uppercase tracking-[0.3em] font-light animate-glow-text-soft">About Us</span>
            </div>

            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-white mb-8 tracking-tight leading-[1.1] animate-title-reveal-smooth animation-delay-200">
              A Legacy of<br />
              Investment <span className="text-[#D4AF37] animate-text-shimmer-flow">Excellence</span>
            </h2>

            <p className="text-white text-lg mb-10 leading-relaxed font-light opacity-90 animate-fade-in-smooth animation-delay-400">
              {aboutContent.description}
            </p>

            {/* Key Points */}
            <div className="space-y-4 mb-12">
              {[
                'Alternative Investments',
                'Strategic Partnerships',
                'Active Asset Management',
                'Global Expertise'
              ].map((item, idx) => (
                <div 
                  key={idx} 
                  className="flex items-center gap-4 group/item animate-list-reveal-smooth"
                  style={{ animationDelay: `${(idx + 6) * 100}ms` }}
                >
                  <CheckCircle2 className="w-5 h-5 text-[#D4AF37] stroke-[1px] flex-shrink-0 transition-all duration-700 group-hover/item:scale-125 group-hover/item:rotate-12 group-hover/item:drop-shadow-[0_0_12px_rgba(212,175,55,0.7)]" />
                  <span className="text-white group-hover/item:text-[#D4AF37] transition-colors duration-700 font-light">{item}</span>
                </div>
              ))}
            </div>

            <Button
              onClick={() => scrollToSection('#story')}
              className="group relative bg-transparent border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#020617] px-10 py-7 font-serif text-lg tracking-wide transition-all duration-1000 ease-out hover:scale-105 overflow-hidden animate-button-reveal-smooth animation-delay-1000"
            >
              <span className="relative z-10 flex items-center">
                Discover Our Story
                <ArrowRight className="ml-3 w-5 h-5 transition-transform duration-1000 ease-out group-hover:translate-x-3" />
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1500" />
            </Button>
          </div>
        </div>

        {/* Why Choose Us Section */}
        <div className="mt-32 pt-32 border-t border-white/10">
          <div className="text-center mb-16 animate-fade-in-smooth">
            <h3 className="font-serif text-3xl md:text-4xl text-white mb-4 tracking-tight">Why Partner With Us</h3>
            <p className="text-[#94A3B8] max-w-2xl mx-auto font-light">Discover the advantages that set Rosevilla Group apart in alternative investments</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {aboutContent.whyChooseUs.map((item, idx) => (
              <div
                key={idx}
                className="group bg-[#0f172a]/50 backdrop-blur-sm border border-white/10 hover:border-[#D4AF37]/60 p-8 transition-all duration-1000 hover:scale-105 hover:bg-[#0f172a]/70 animate-card-rise-smooth relative overflow-hidden"
                style={{ animationDelay: `${idx * 150}ms` }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/0 to-[#D4AF37]/8 opacity-0 group-hover:opacity-100 transition-opacity duration-1000" />
                <div className="absolute top-0 right-0 w-20 h-20 bg-[#D4AF37]/8 rounded-full blur-2xl group-hover:scale-[2] transition-transform duration-1000" />
                
                {/* Shimmer on Hover */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1500" />
                
                <div className="relative z-10">
                  <div className="w-12 h-12 bg-[#D4AF37]/10 rounded flex items-center justify-center mb-6 group-hover:bg-[#D4AF37]/25 transition-all duration-1000 group-hover:scale-110">
                    <span className="text-[#D4AF37] text-2xl font-serif">{idx + 1}</span>
                  </div>
                  <h4 className="text-white font-serif text-xl mb-3 group-hover:text-[#D4AF37] transition-colors duration-700">{item.title}</h4>
                  <p className="text-[#94A3B8] text-sm font-light leading-relaxed group-hover:text-white/80 transition-colors duration-700">{item.description}</p>
                </div>
                
                {/* Animated Bottom Border */}
                <div className="absolute bottom-0 left-0 w-0 h-[2px] bg-gradient-to-r from-[#D4AF37] to-transparent group-hover:w-full transition-all duration-1000" />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CSS Animations */}
      <style jsx>{`
        @keyframes gradient-flow-smooth {
          0%, 100% { opacity: 0.08; }
          50% { opacity: 0.15; }
        }

        @keyframes gradient-wave-soft {
          0%, 100% { transform: translateY(0); opacity: 0.03; }
          50% { transform: translateY(-20px); opacity: 0.08; }
        }

        @keyframes drift-smooth-1 {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.5; }
          50% { transform: translate(60px, -70px) scale(1.3); opacity: 0.8; }
        }

        @keyframes drift-smooth-2 {
          0%, 100% { transform: translate(0, 0); opacity: 0.4; }
          50% { transform: translate(-50px, 80px); opacity: 0.7; }
        }

        @keyframes drift-smooth-3 {
          0%, 100% { transform: translate(0, 0) rotate(0deg); opacity: 0.4; }
          50% { transform: translate(70px, 60px) rotate(180deg); opacity: 0.75; }
        }

        @keyframes drift-smooth-4 {
          0%, 100% { transform: translate(0, 0); opacity: 0.5; }
          50% { transform: translate(-40px, -50px); opacity: 0.8; }
        }

        @keyframes drift-smooth-5 {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.45; }
          50% { transform: translate(50px, -60px) scale(1.2); opacity: 0.75; }
        }

        @keyframes drift-smooth-6 {
          0%, 100% { transform: translate(0, 0); opacity: 0.35; }
          50% { transform: translate(-55px, 65px); opacity: 0.65; }
        }

        @keyframes beam-smooth-1 {
          0%, 100% { opacity: 0; transform: translateY(-100%); }
          50% { opacity: 0.2; transform: translateY(100%); }
        }

        @keyframes beam-smooth-2 {
          0%, 100% { opacity: 0; transform: translateY(100%); }
          50% { opacity: 0.15; transform: translateY(-100%); }
        }

        @keyframes pulse-ambient-1 {
          0%, 100% { opacity: 0.08; transform: scale(1); }
          50% { opacity: 0.15; transform: scale(1.3); }
        }

        @keyframes pulse-ambient-2 {
          0%, 100% { opacity: 0.06; transform: scale(1); }
          50% { opacity: 0.12; transform: scale(1.4); }
        }

        @keyframes scale-in-smooth {
          0% { opacity: 0; transform: scale(0.9); }
          100% { opacity: 1; transform: scale(1); }
        }

        @keyframes slide-from-right-smooth {
          0% { opacity: 0; transform: translateX(50px); }
          100% { opacity: 1; transform: translateX(0); }
        }

        @keyframes fade-slide-in-smooth {
          0% { opacity: 0; transform: translateX(-20px); }
          100% { opacity: 1; transform: translateX(0); }
        }

        @keyframes width-expand-smooth {
          0% { width: 0; opacity: 0; }
          100% { width: 4rem; opacity: 1; }
        }

        @keyframes glow-text-soft {
          0%, 100% { text-shadow: 0 0 15px rgba(212, 175, 55, 0.3); }
          50% { text-shadow: 0 0 25px rgba(212, 175, 55, 0.5); }
        }

        @keyframes title-reveal-smooth {
          0% { opacity: 0; transform: translateY(30px); filter: blur(8px); }
          100% { opacity: 1; transform: translateY(0); filter: blur(0); }
        }

        @keyframes text-shimmer-flow {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.85; text-shadow: 0 0 20px rgba(212, 175, 55, 0.4); }
        }

        @keyframes fade-in-smooth {
          0% { opacity: 0; transform: translateY(20px); }
          100% { opacity: 0.9; transform: translateY(0); }
        }

        @keyframes list-reveal-smooth {
          0% { opacity: 0; transform: translateX(-15px); }
          100% { opacity: 1; transform: translateX(0); }
        }

        @keyframes button-reveal-smooth {
          0% { opacity: 0; transform: scale(0.9); }
          100% { opacity: 1; transform: scale(1); }
        }

        @keyframes card-rise-smooth {
          0% { opacity: 0; transform: translateY(40px); }
          100% { opacity: 1; transform: translateY(0); }
        }

        @keyframes border-draw-smooth {
          0% { opacity: 0; }
          100% { opacity: 1; }
        }

        @keyframes pulse-corner-soft {
          0%, 100% { opacity: 0.25; transform: scale(1); }
          50% { opacity: 0.4; transform: scale(1.2); }
        }

        @keyframes text-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.8; }
        }

        .animate-gradient-flow-smooth { animation: gradient-flow-smooth 12s ease-in-out infinite; }
        .animate-gradient-wave-soft { animation: gradient-wave-soft 15s ease-in-out infinite; }
        .animate-drift-smooth-1 { animation: drift-smooth-1 18s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite; }
        .animate-drift-smooth-2 { animation: drift-smooth-2 20s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite; }
        .animate-drift-smooth-3 { animation: drift-smooth-3 22s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite; }
        .animate-drift-smooth-4 { animation: drift-smooth-4 19s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite; }
        .animate-drift-smooth-5 { animation: drift-smooth-5 21s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite; }
        .animate-drift-smooth-6 { animation: drift-smooth-6 23s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite; }
        .animate-beam-smooth-1 { animation: beam-smooth-1 16s ease-in-out infinite; }
        .animate-beam-smooth-2 { animation: beam-smooth-2 18s ease-in-out infinite; }
        .animate-pulse-ambient-1 { animation: pulse-ambient-1 10s ease-in-out infinite; }
        .animate-pulse-ambient-2 { animation: pulse-ambient-2 12s ease-in-out infinite; }
        .animate-scale-in-smooth { animation: scale-in-smooth 1.5s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        .animate-slide-from-right-smooth { animation: slide-from-right-smooth 1.5s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        .animate-fade-slide-in-smooth { animation: fade-slide-in-smooth 1.2s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        .animate-width-expand-smooth { animation: width-expand-smooth 1s ease-out forwards; }
        .animate-glow-text-soft { animation: glow-text-soft 4s ease-in-out infinite; }
        .animate-title-reveal-smooth { animation: title-reveal-smooth 1.5s cubic-bezier(0.16, 1, 0.3, 1) forwards; opacity: 0; }
        .animate-text-shimmer-flow { animation: text-shimmer-flow 5s ease-in-out infinite; }
        .animate-fade-in-smooth { animation: fade-in-smooth 1.5s cubic-bezier(0.16, 1, 0.3, 1) forwards; opacity: 0; }
        .animate-list-reveal-smooth { animation: list-reveal-smooth 1s cubic-bezier(0.16, 1, 0.3, 1) forwards; opacity: 0; }
        .animate-button-reveal-smooth { animation: button-reveal-smooth 1.2s cubic-bezier(0.68, -0.55, 0.265, 1.55) forwards; opacity: 0; }
        .animate-card-rise-smooth { animation: card-rise-smooth 1.2s cubic-bezier(0.16, 1, 0.3, 1) forwards; opacity: 0; }
        .animate-border-draw-smooth { animation: border-draw-smooth 1.5s ease-out forwards; }
        .animate-pulse-corner-soft { animation: pulse-corner-soft 8s ease-in-out infinite; }
        .animate-text-pulse { animation: text-pulse 4s ease-in-out infinite; }
        
        .animation-delay-200 { animation-delay: 0.2s; }
        .animation-delay-400 { animation-delay: 0.4s; }
        .animation-delay-500 { animation-delay: 0.5s; }
        .animation-delay-1000 { animation-delay: 1s; }
      `}</style>
    </section>
  );
};

export default About;
